<template>
  <div id="changeInsurance" class="changeInsurance">
    <!-- card number form -->
    <div v-if="isCardNumberStep" class="card__form">
      <FormTemplate
        class="changeInsurance__form"
        :schema="schema"
        :model="model"
        :submittButton="{ text: 'Continuar', disabled: finalClick }"
        @submit.prevent="submitCardNumber"
      />
    </div>

    <div v-else class="section__options">
      <!-- search bar -->
      <div class="options__search">
        <VInput v-model="searchModel" :placeholder="`Buscar`" size="sm" />
      </div>

      <!-- options list -->
      <div class="options__list">
        <VList :items="currentOptions">
          <template v-slot:content="slotProps">
            <div id="changeInsurance-link" @click="setAnswer(slotProps.data)">
              {{ slotProps.data.nome || slotProps.data.valor | capitalizeText }}
            </div>
          </template>
        </VList>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import { removeAccents } from '@/utils/helpers';
import { dataLayersMixin } from '@/utils/mixins';
import searchObjectArray from '@/utils/helpers/search/searchObjectArray';
import VList from '@/components/commons/VList.vue';
import VInput from '@/components/commons/VInput.vue';
import FormTemplate from '@/components/commons/forms/FormTemplate.vue';
import { handlePaymentMixin } from '@/utils/mixins/handlePayment.mixin';
import { PaymentTypeClient } from '@/utils/enums/paymentMagicNumberEnum';
import debounce from 'lodash/debounce';

export default {
  name: 'InsuranceOptions',

  mixins: [dataLayersMixin, handlePaymentMixin],

  components: {
    VList,
    VInput,
    FormTemplate,
  },
  watch: {
    cardNumber() {
      this.debounceFieldChange()();
    },
  },

  data() {
    return {
      finalClick: false,
      step: {
        index: 0,
        name: 'Escolha seu convênio',
        options: [],
      },
      paymentPayload: {},
      insurancePlanName: '',
      variableSuperiorId: '',
      model: {
        cardNumberHealthInsurance: '',
      },
      searchModel: '',
      schema: {
        fields: [
          {
            type: 'input',
            id: 'changeInsurance-card-number',
            label: 'Número da carteirinha',
            model: 'cardNumberHealthInsurance',
            rules: 'required',
            readonly: false,
            ShowFieldForInCompany: true,
          },
        ],
      },
    };
  },

  created() {
    this.mostCommonPagesAnalytics({
      eventType: 'funnel_step',
      currentStep: 'Escolha o convênio',
    });

    this.$router.replace({
      name: 'change-insurance',
      params: {
        path: '',
        header: {
          hidePreviousBtn: false,
          title: 'Escolha seu convenio',
        },
      },
    });
  },

  async mounted() {
    this.$router.replace({
      name: 'change-insurance',
      params: {
        path: 'cliente',
        header: {
          hidePreviousBtn: false,
          title: 'Escolha seu convenio',
        },
      },
    });
    await this.setInitialOptions();
  },

  computed: {
    ...mapState({
      admission: ({ currentAdmission }) => currentAdmission.data,
      admissionPayment: ({ currentAdmission }) => currentAdmission.data.payments[0],
      admissionOrigin: ({ currentAdmission }) => currentAdmission.data.source.origin,
      healthInsuranceList: ({ healthInsurance }) => healthInsurance.data.healthInsuranceList,
      healthInsuranceVariables: ({ healthInsurance }) =>
        healthInsurance.data.healthInsuranceVariables,
      healthInsuranceCardNumber: ({ healthInsurance }) =>
        healthInsurance.data.healthInsuranceCardInfos?.cardNumber,
    }),

    cardNumber() {
      return this.model.cardNumberHealthInsurance;
    },

    isCardNumberStep() {
      const haveVariables = !!this.healthInsuranceVariables?.length;

      return haveVariables
        ? this.step.index > this.healthInsuranceVariables.length
        : this.step.index > 0;
    },

    isFirstStep() {
      return this.step.index === 0;
    },

    isLastStep() {
      return this.step.index > this.healthInsuranceVariables?.length;
    },

    isVariablesSteps() {
      return !this.isFirstStep && !this.isLastStep;
    },

    currentOptions() {
      const key = this.step.index === 0 ? 'nome' : 'valor';

      return searchObjectArray(this.step.options, this.searchModel, key);
    },

    currentVariable() {
      return this.healthInsuranceVariables.find(({ posicao }) => posicao === this.step.index);
    },
  },

  methods: {
    ...mapActions([
      'getHealthInsuranceByBrandId',
      'getHealthInsuranceVariables',
      'getLatestHealthInsuranceCards',
    ]),
    ...mapMutations({
      setClearHealthInsuranceLists: 'SET_CLEAR_HEALTH_INSURANCE_LISTS',
      setHealthInsuranceCardNumber: 'SET_HEALTH_INSURANCE_CARD_NUMBER',
    }),

    async setInitialOptions() {
      if (!this.healthInsuranceCardNumber) {
        await this.setClearHealthInsuranceLists();
        await this.getHealthInsuranceByBrandId();
      }

      this.model.cardNumberHealthInsurance = this.healthInsuranceCardNumber;

      this.step = {
        index: 0,
        name: 'Escolha seu convênio',
        options: this.healthInsuranceList,
      };
    },

    debounceFieldChange() {
      return debounce(() => {
        this.setHealthInsuranceCardNumber(this.model.cardNumberHealthInsurance);
      }, 3000);
    },

    submitCardNumber($event) {
      this.setAnswer($event.target[0].value);
    },

    async setAnswer(answer) {
      const path = this.$route.params.path;

      if (
        path === 'plano' ||
        path === 'tipo-contrato' ||
        path === 'produto' ||
        path === 'rede-atendimento'
      ) {
        this.insurancePlanName = answer.valor;
      }

      if (this.isLastStep) {
        // Avoid user to click multiple times on create payment button
        // If you dont understand please dont touch
        this.finalClick = true;

        this.paymentPayload.details = {
          ...this.paymentPayload.details,
          cardNumber: answer,
        };

        await this.admissionPaymentHandle();

        return this.goNextStep('insurance_card_step');
      }

      if (this.isFirstStep) {
        const { idCliente, nome } = answer;

        this.paymentPayload = {
          clientId: idCliente,
          clientName: nome,
        };
      } else if (this.isVariablesSteps) {
        // Used to filter next variable step options
        this.variableSuperiorId = String(answer.id);

        this.setNegotiationVariables(answer);
      }

      this.nextInsuranceStep();
    },

    setNegotiationVariables(answer) {
      const variableQuestionId = this.currentVariable.id;
      const variableAnswerId = answer.id;
      const negotiationVariables = `${variableQuestionId}:${variableAnswerId}`;
      const hasNegotiationVariables = !!this.paymentPayload?.details?.negotiationVariables;

      if (hasNegotiationVariables) {
        this.paymentPayload.details.negotiationVariables += `,${negotiationVariables}`;

        return;
      }

      this.paymentPayload.details = {
        negotiationVariables: `${negotiationVariables}`,
      };
    },

    async admissionPaymentHandle() {
      const clientId = String(this.paymentPayload.clientId);

      if (clientId !== PaymentTypeClient.PRIVATE) {
        const params = {
          ...this.paymentPayload,
          uuid: this.admissionPayment.uuid,
          clientId,
          status: 'PENDING',
          details: {
            negotiationVariables: '',
            cardExpirationDate: null,
            ...this.paymentPayload.details,
            type: 'HEALTH_INSURANCE',
          },
        };

        return await this.handlePayment_mixin(params);
      }
    },
    async nextInsuranceStep() {
      if (this.isFirstStep) {
        const { clientId } = this.paymentPayload;
        await this.getHealthInsuranceVariables({ clientId, admissionOrigin: this.admissionOrigin });
      }

      this.step.index += 1;

      if (!this.isLastStep) {
        this.setVariableStep();
      } else {
        this.setCardNumberStep();
      }

      this.insuranceRouteBuilder();
    },

    setVariableStep() {
      const { nome, valores } = this.currentVariable;
      let filteredValues = null;

      if (this.variableSuperiorId) {
        filteredValues = valores.filter((value) => {
          if (!value.idSuperior) {
            return true;
          }

          return value.idSuperior === this.variableSuperiorId;
        });
      }

      this.step.name = nome;
      this.step.options = filteredValues || valores;
    },

    setCardNumberStep() {
      this.step.name = 'Carteirinha';
      this.step.options = [];
    },

    // Builds next route's parameters/ increases current step index and pushes route
    insuranceRouteBuilder() {
      const routePathName = this.getNormalizedRoutePathName(this.step.name);
      const pushStrategy = this.$route.params.path === 'carteirinha' ? 'push' : 'replace';
      const route = {
        name: 'change-insurance',
        params: {
          path: routePathName,
          header: {
            hidePreviousBtn: false,
            title: this.step.name,
          },
        },
      };

      this.$router[pushStrategy](route);
    },

    getNormalizedRoutePathName(route) {
      return removeAccents(route).toLowerCase().split(' ').join('-');
    },
  },

  async beforeRouteUpdate(to, from, next) {
    const isClientePath = to.params.path === 'cliente';

    if (isClientePath) {
      await this.setInitialOptions();
    }

    this.searchModel = '';

    next();
  },
};
</script>

<style lang="scss" scoped>
.changeInsurance {
  width: 100%;

  .section {
    &__private {
      padding-bottom: 56px;
    }
  }

  .options {
    &__search {
      padding: 16px 0;
    }

    &__list {
      height: 75vh;
    }
  }

  a {
    width: 100%;
    text-align: left;
  }
}
</style>
